import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { safeAnchorHref } from '../../../lib/link';
import { cn } from '../../../lib/utils';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraStatusDot } from '../ensigns/status-dot';
import { SpecraHeading, SpecraText } from '../ensigns/typography';
const metricToneClass = {
    neutral: 'text-foreground',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
    info: 'text-info'
};
/** One KPI value with semantic state, label, and context. */ export function SpecraMetricItem({ className, density = 'default', loading = false, metric, renderLink, ...props }) {
    const fallbackHref = metric.href ? safeAnchorHref(metric.href) : undefined;
    // A loading tile is inert: never a link, and never a button that could submit a form.
    const Component = loading ? 'div' : fallbackHref ? 'a' : metric.onClick ? 'button' : 'div';
    const interactiveProps = fallbackHref ? {
        href: fallbackHref
    } : metric.onClick ? {
        onClick: metric.onClick,
        type: 'button'
    } : {};
    const interactive = !loading && Boolean(metric.href || metric.onClick);
    const tone = metric.tone ?? 'neutral';
    const itemClassName = cn('block min-w-0 px-[var(--space-5)] py-[var(--space-4)] text-left', interactive && `cursor-pointer hover:bg-accent/50 ${specraFocusRing}`, className);
    const body = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-[var(--space-kpi-gap)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraStatusDot, {
                        tone: tone
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        className: "text-[length:var(--fs-ui-label)] font-medium uppercase tracking-[var(--ls-ui-eyebrow)]",
                        size: "micro",
                        tone: "muted",
                        children: metric.label
                    })
                ]
            }),
            loading ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        "aria-hidden": "true",
                        className: "mt-[var(--space-2)] h-[var(--fs-h3)] w-16 animate-pulse rounded-[var(--radius-sm)] bg-muted"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "sr-only",
                        children: "Loading"
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraHeading, {
                className: cn('mt-[var(--space-2)] [overflow-wrap:anywhere]', metric.muted ? 'text-muted-foreground' : metricToneClass[tone]),
                // A muted value is words ("No data yet"), so it reads at section size.
                size: metric.muted ? 'sm' : density === 'compact' ? 'md' : 'lg',
                children: metric.value
            }),
            metric.note ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                className: "mt-[var(--space-1-5)] text-[color:var(--text-faint)]",
                size: "meta",
                tone: "muted",
                children: metric.note
            }) : null
        ]
    });
    if (!loading && metric.href && renderLink) {
        return renderLink({
            ...props,
            children: body,
            className: itemClassName,
            href: metric.href
        });
    }
    return /*#__PURE__*/ _jsx(Component, {
        className: itemClassName,
        ...loading ? {} : interactiveProps,
        ...props,
        children: body
    });
}
